import PropTypes from "prop-types";
import styled from "styled-components";

import Pagination from "../Pagination/Pagination";
import MatterLoader from "../Loader/MatterLoader";
import { PaginationContainer } from "../../pages/Home/HomeStyled";
import SortIcon from "../../assets/images/sorting.svg";

/**
 * One activity list, sized to sit two-up on the matter dashboard.
 *
 * Deliberately not the wide `Table` the Home tabs use (Matthew,
 * 2026-07-30): seven columns across half a screen reads like a
 * spreadsheet and forces every value into a narrow strip. Here each row is
 * the document name with its details stacked underneath and the status
 * pinned right, so nothing has to be sacrificed to fit -- the row grows
 * downward instead of sideways.
 *
 * The card is a shell only. It knows nothing about VA uploads,
 * assignments, tasks or reviews; callers hand it already-shaped rows via
 * an adapter (see `activityRows.js`), which is what keeps four visually
 * identical lists from becoming four components.
 */

const Card = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 12px 14px 8px;
  /* Enough that an empty card still reads as a card, not so much that four
     of them push the rest of the dashboard off screen. */
  min-height: 150px;
  /* Without this a long unbroken filename widens the grid column instead
     of being truncated inside it. */
  min-width: 0;
`;

const Head = styled.header`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f2f4;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #374151;
`;

const Count = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #8a93a3;
`;

const SortButton = styled.button`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: none;
  padding: 2px 4px;
  cursor: pointer;
  font-size: 11px;
  color: #6b7280;

  &:hover {
    color: #374151;
  }
`;

const Rows = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
`;

const Row = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 9px 2px;
  border-bottom: 1px solid #f6f7f8;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: ${({ $clickable }) => ($clickable ? "#fafbfc" : "transparent")};
  }
`;

// min-width: 0 is what actually lets the children ellipsis: a flex item
// defaults to its content's width, so without it long text pushes the
// status pill out of the card rather than truncating.
const Main = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Meta = styled.div`
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.35;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Side = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const Empty = styled.div`
  padding: 22px 2px;
  text-align: center;
  font-size: 12px;
  color: #9099a8;
`;

const ActivityCard = ({
  title,
  rows,
  adapt,
  emptyMessage,
  isLoading = false,
  count,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
  onRowClick,
  onSort,
  headerExtra,
}) => {
  const hasRows = rows.length > 0;

  return (
    // Named so each card is its own landmark: four unlabelled lists side by
    // side are indistinguishable to a screen reader.
    <Card aria-label={title}>
      {isLoading && (
        <MatterLoader message="Loading..." offsetTop={0} absolute={true} />
      )}

      <Head>
        <Title>{title}</Title>
        {/* Withheld until the list has loaded: a card that says "(0)" while
            still fetching claims the case is empty when it is not. */}
        {typeof count === "number" && <Count>({count})</Count>}
        {headerExtra}
        {onSort && (
          <SortButton type="button" onClick={onSort} aria-label={`Sort ${title}`}>
            <img src={SortIcon} alt="" width={10} height={10} />
            Name
          </SortButton>
        )}
      </Head>

      {hasRows ? (
        <Rows>
          {rows.map((record) => {
            const { key, name, lines = [], status, actions } = adapt(record);
            return (
              <Row
                key={key}
                $clickable={Boolean(onRowClick)}
                onClick={onRowClick ? () => onRowClick(record) : undefined}
              >
                <Main>
                  {/* title= gives back in a tooltip whatever the ellipsis
                      takes away, so truncating costs no information. */}
                  <Name title={name}>{name}</Name>
                  {lines
                    .filter(Boolean)
                    .map((line, index) => (
                      <Meta key={index} title={typeof line === "string" ? line : undefined}>
                        {line}
                      </Meta>
                    ))}
                </Main>
                <Side>
                  {status}
                  {actions}
                </Side>
              </Row>
            );
          })}
        </Rows>
      ) : (
        <Empty>{emptyMessage}</Empty>
      )}

      {/* Only once there is more than one page. A pager under a card of
          three rows is chrome the card cannot afford. */}
      {hasRows && count > pageSize && (
        <PaginationContainer>
          <Pagination
            total={count}
            current={currentPage}
            pageSize={pageSize}
            onChange={(page) => onPageChange(page)}
            adjustable={false}
          />
        </PaginationContainer>
      )}
    </Card>
  );
};

ActivityCard.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  /** record => { key, name, lines[], status, actions } */
  adapt: PropTypes.func.isRequired,
  emptyMessage: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  count: PropTypes.number,
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onRowClick: PropTypes.func,
  onSort: PropTypes.func,
  headerExtra: PropTypes.node,
};

export default ActivityCard;
